import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, Trash2, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";

interface MediaItem {
  id: string;
  filename: string;
  url: string;
  mime_type: string;
  size_bytes: number;
}

export default function MediaLibrary() {
  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/media");
    setItems(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/admin/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Immagine caricata");
      await load();
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/admin/media/${id}`);
      toast.success("Immagine eliminata");
      await load();
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const copy = (url: string) => {
    navigator.clipboard.writeText(mediaUrl(url));
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="media-library">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">Contenuti</p>
          <h1 className="font-display font-black tracking-tighter text-4xl">Media Library</h1>
          <p className="text-muted-foreground text-sm mt-1">JPG, PNG o WEBP · max 10 MB</p>
        </div>
        <button
          data-testid="media-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="shrink-0 flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Carica
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
        />
      </div>

      {items === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 text-center text-muted-foreground" data-testid="media-empty">
          Nessuna immagine caricata.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="group border border-white/10 bg-card" data-testid="media-item">
              <div className="aspect-square overflow-hidden bg-black/40">
                <img src={mediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <p className="text-xs truncate text-muted-foreground" title={m.filename}>{m.filename}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => copy(m.url)} className="flex-1 flex items-center justify-center gap-1 border border-white/15 py-1.5 text-[10px] uppercase tracking-wider hover:text-secondary transition-colors" data-testid="media-copy-button">
                    {copied === m.url ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} URL
                  </button>
                  <button onClick={() => remove(m.id)} className="p-1.5 border border-white/15 text-muted-foreground hover:text-primary transition-colors" data-testid="media-delete-button">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
